/*
  L'Apprêt · la-mesures.js
  Lecture du vestiaire, des mesures à plat, et rapprochement avec « votre vêtement préféré ».
  Utilisé par : la section la-comparateur, le snippet la-badge-prefere et la section la-studio.
  Aucune dépendance.
*/
(function () {
  'use strict';

  var CLE_STOCKAGE = 'la-vetement-prefere';
  var CLE_CACHE = 'la-vestiaire-cache-2';
  var DUREE_CACHE_MS = 10 * 60 * 1000;

  var IDS_MESURES = ['epaules', 'largeur_epaules', 'poitrine', 'manches', 'manches_raglan', 'longueur',
    'taille_plat', 'fourche', 'entrejambe', 'cheville', 'ouverture'];

  /* Manches raglan : mesurées du début du col jusqu'au bout de la manche, elles remplacent
     épaules et manches. Pour les comparer à des manches classiques, on ramène les deux à la
     longueur « milieu du dos → bout de la manche » (yuki-take) en ajoutant la distance entre
     le milieu du dos et le début du col, pièce à plat. */
  var COL_RAGLAN_CM = 9;

  /* Tolérances en cm : tolMoins quand la pièce est plus petite que la référence,
     tolPlus quand elle est plus grande. Un entrejambe trop long se reprend, un trop court non. */
  var CHAMPS = {
    haut: [
      { id: 'poitrine', label: 'Poitrine', aide: "d'une couture d'aisselle à l'autre", poids: 3, tolMoins: 2, tolPlus: 2.5 },
      { id: 'largeur_epaules', label: "Largeur d'épaules", aide: "distance entre les deux coutures d'épaules", poids: 2, tolMoins: 2, tolPlus: 2.5 },
      { id: 'manches', label: 'Manches', aide: "de la couture d'épaule à l'extrémité du poignet", poids: 1.5, tolMoins: 2, tolPlus: 3 },
      { id: 'longueur', label: 'Longueur', aide: 'du haut du col au bas du vêtement', poids: 1.5, tolMoins: 2.5, tolPlus: 3.5 },
      { id: 'epaules', label: 'Épaules', aide: "du col à la couture de l'épaule", poids: 0.5, tolMoins: 1.5, tolPlus: 1.5, facultatif: true },
      { id: 'manches_raglan', label: 'Manches raglan', aide: "si la pièce n'a pas de couture d'épaule : du début du col au bout de la manche, à la place des épaules et des manches", poids: 0, tolMoins: 2, tolPlus: 3, facultatif: true, entree: true }
    ],
    pantalon: [
      { id: 'taille_plat', label: 'Taille', aide: "d'un bord à l'autre de la ceinture", poids: 3, tolMoins: 1.5, tolPlus: 1.5 },
      { id: 'fourche', label: 'Fourche', aide: "du haut de la ceinture au croisement des coutures à l'entrejambe", poids: 2, tolMoins: 2, tolPlus: 2 },
      { id: 'entrejambe', label: 'Entrejambe', aide: "du croisement des coutures à l'entrejambe jusqu'au bas", poids: 1.2, tolMoins: 2.5, tolPlus: 8 },
      { id: 'cheville', label: 'Cheville', aide: 'largeur du bas de jambe, bord à bord', poids: 1, tolMoins: 1.5, tolPlus: 1.5 },
      { id: 'longueur', label: 'Longueur', aide: 'du haut de la ceinture au bas', poids: 1, tolMoins: 2.5, tolPlus: 8, facultatif: true }
    ],
    short: [
      { id: 'taille_plat', label: 'Taille', aide: "d'un bord à l'autre de la ceinture", poids: 3, tolMoins: 1.5, tolPlus: 1.5 },
      { id: 'fourche', label: 'Fourche', aide: "du haut de la ceinture au croisement des coutures à l'entrejambe", poids: 2, tolMoins: 2, tolPlus: 2 },
      { id: 'entrejambe', label: 'Entrejambe', aide: "du croisement des coutures à l'entrejambe jusqu'au bas", poids: 1, tolMoins: 2.5, tolPlus: 4 },
      { id: 'ouverture', label: 'Ouverture', aide: 'largeur du bas de jambe, bord à bord', poids: 1, tolMoins: 2, tolPlus: 2 },
      { id: 'longueur', label: 'Longueur', aide: 'du haut de la ceinture au bas', poids: 1, tolMoins: 2.5, tolPlus: 4, facultatif: true }
    ]
  };

  var GROUPES = {
    haut: { label: 'Un haut', nom: 'haut' },
    pantalon: { label: 'Un pantalon', nom: 'pantalon' },
    short: { label: 'Un short', nom: 'short' }
  };

  /* « Comme un gant » : au moins trois mesures comparées, toutes à PARFAIT_CM près. */
  var PARFAIT_CM = 1;
  var PARFAIT_MIN_MESURES = 3;

  var NIVEAUX = {
    'parfait': 'Comme un gant',
    'tres-proche': 'Très proche',
    'proche': 'Proche',
    'a-considerer': 'À considérer',
    'eloigne': 'Éloigné'
  };

  var PHRASES = {
    poitrine: ['plus ample de {d} cm à la poitrine ({t} cm de tour)', 'plus ajusté de {d} cm à la poitrine ({t} cm de tour)'],
    largeur_epaules: ['épaules plus larges de {d} cm', 'épaules plus étroites de {d} cm'],
    manches: ['manches plus longues de {d} cm', 'manches plus courtes de {d} cm'],
    longueur: ['plus long de {d} cm', 'plus court de {d} cm'],
    epaules: ["couture d'épaule plus longue de {d} cm", "couture d'épaule plus courte de {d} cm"],
    taille_plat: ['taille plus large de {d} cm ({t} cm de tour)', 'taille plus serrée de {d} cm ({t} cm de tour)'],
    fourche: ['fourche plus longue de {d} cm, taille plus haute', 'fourche plus courte de {d} cm, taille plus basse'],
    entrejambe: ['entrejambe plus long de {d} cm', 'entrejambe plus court de {d} cm'],
    cheville: ['bas de jambe plus large de {d} cm', 'bas de jambe plus étroit de {d} cm'],
    ouverture: ['ouverture plus large de {d} cm', 'ouverture plus étroite de {d} cm']
  };

  /* ---------- Lecture des valeurs ---------- */

  function nombre(v) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return isFinite(v) ? v : null;
    if (typeof v === 'object' && v.value !== undefined) return nombre(v.value);
    var s = String(v).replace(',', '.').match(/-?\d+(\.\d+)?/);
    return s ? parseFloat(s[0]) : null;
  }

  function texte(v) {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number') return String(v);
    if (Array.isArray(v)) return v.map(texte).filter(Boolean).join(', ');
    if (typeof v === 'object' && v.value !== undefined) return texte(v.value);
    return '';
  }

  function estNombre(n) { return typeof n === 'number' && isFinite(n); }

  /* Un identifiant de métaobjet (« tres-bon-etat ») devient un libellé lisible (« Très bon état »). */
  function libelle(s) {
    if (!s || !/^[a-z0-9-]+$/.test(s)) return s;
    s = s.replace(/-/g, ' ').replace(/\bl etat\b/g, 'l\u2019état').replace(/\betat\b/g, 'état').replace(/\btres\b/g, 'très');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function normaliser(brut) {
    var c = brut.champs || {};
    var m = {};
    IDS_MESURES.forEach(function (id) { m[id] = nombre(c[id]); });
    /* manches_raglan est un interrupteur (vrai, « Oui ») : la mesure est alors dans « manches »,
       prise du début du col au bout de la manche, et remplace épaules et largeur d'épaules. */
    var rag = c.manches_raglan;
    var ragActif = rag === true || /^(oui|vrai|true|yes)$/i.test(String(rag === null || rag === undefined ? '' : rag).trim());
    if (ragActif) {
      m.manches_raglan = m.manches;
      m.manches = null;
      m.epaules = null;
      m.largeur_epaules = null;
    } else if (!(m.manches_raglan >= 20)) {
      m.manches_raglan = null;
    }
    var variante = texte(brut.variante);
    var p = {
      id: brut.id,
      handle: brut.handle,
      titre: texte(brut.titre),
      url: brut.url,
      marque: texte(brut.marque),
      marqueTexte: texte(brut.marque_texte),
      type: texte(brut.type),
      prix: nombre(brut.prix),
      prixBarre: nombre(brut.prix_barre),
      dispo: brut.dispo !== false,
      description: texte(brut.description),
      images: (brut.images || []).map(function (u) { return u && u.indexOf('//') === 0 ? (window.location.protocol || 'https:') + u : u; }),
      tags: brut.tags || [],
      taille: texte(c.taille_label) || (variante && variante !== 'Default Title' ? variante : ''),
      etat: libelle(texte(c.etat)),
      matiere: texte(c.matiere),
      origine: texte(c.origine),
      m: m
    };
    p.groupe = groupeDe(p);
    if (p.groupe === 'short' && !estNombre(m.ouverture) && estNombre(m.cheville)) m.ouverture = m.cheville;
    return p;
  }

  function groupeDe(p) {
    var t = ((p.type || '') + ' ' + (p.titre || '')).toLowerCase();
    var m = p.m;
    if (/jupe|skirt|robe\b/.test(t)) return null;
    if (estNombre(m.poitrine)) return 'haut';
    if (/\bshorts?\b|bermuda/.test(t)) return 'short';
    if (/jean|pantalon|pants|trouser|chino|hakama/.test(t)) return 'pantalon';
    if (estNombre(m.entrejambe)) return m.entrejambe >= 45 ? 'pantalon' : 'short';
    if (estNombre(m.taille_plat) && estNombre(m.longueur)) return m.longueur >= 75 ? 'pantalon' : 'short';
    if (estNombre(m.largeur_epaules) || estNombre(m.manches) || estNombre(m.manches_raglan)) return 'haut';
    return null;
  }

  function aDesMesures(p) {
    return IDS_MESURES.some(function (id) { return estNombre(p.m[id]); });
  }

  /* ---------- Rapprochement ---------- */

  function fr(n) {
    var r = Math.round(Math.abs(n) * 2) / 2;
    return String(r).replace('.', ',');
  }

  function phrase(def, d, yuki, groupe) {
    if (Math.abs(d) < 0.5) return def.label + ' : identique';
    var modele = PHRASES[def.id][d > 0 ? 0 : 1];
    var s = modele.replace('{d}', fr(d)).replace('{t}', fr(d * 2));
    if (yuki) s += ', mesurées depuis le milieu du dos';
    if (d >= 2 && (def.id === 'entrejambe' || (def.id === 'longueur' && groupe !== 'haut'))) s += ', un ourlet règle ça';
    if (def.id === 'largeur_epaules' && d >= 4) s += ' (épaules tombantes, sans doute voulues)';
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /*
    ref : mesures du vêtement préféré ({poitrine: 52, ...})
    m   : mesures de la pièce
    Retourne null si moins de deux mesures sont comparables.
  */
  function yukiDe(x) {
    if (estNombre(x.manches_raglan)) return x.manches_raglan + COL_RAGLAN_CM;
    if (estNombre(x.largeur_epaules) && estNombre(x.manches)) return x.largeur_epaules / 2 + x.manches;
    return null;
  }

  function comparer(ref, m, groupe) {
    var defs = CHAMPS[groupe];
    if (!defs || !ref || !m) return null;
    var yRef = groupe === 'haut' ? yukiDe(ref) : null;
    var yPiece = groupe === 'haut' ? yukiDe(m) : null;
    var yuki = yRef !== null && yPiece !== null;
    var sansLongueur = groupe !== 'haut' && estNombre(ref.entrejambe) && estNombre(m.entrejambe);
    var num = 0, den = 0, lignes = [];

    defs.forEach(function (def) {
      if (def.id === 'longueur' && sansLongueur) return;
      if (def.entree) return;
      var r, v, estYuki = false;
      if (def.id === 'manches' && yuki) {
        r = yRef;
        v = yPiece;
        estYuki = true;
      } else {
        r = ref[def.id];
        v = m[def.id];
      }
      if (!estNombre(r) || !estNombre(v)) return;
      var d = v - r;
      var tol = d >= 0 ? def.tolPlus : def.tolMoins;
      var n = Math.abs(d) / tol;
      num += def.poids * Math.min(n * n, 16);
      den += def.poids;
      lignes.push({
        id: def.id,
        label: def.label,
        ref: r,
        val: v,
        d: d,
        n: n,
        ton: n <= 0.5 ? 'ok' : (n <= 1 ? 'leger' : 'ecart'),
        texte: phrase(def, d, estYuki, groupe),
        brut: estYuki ? m.manches : v
      });
    });

    if (lignes.length < 2 || den === 0) return null;
    var ecart = Math.sqrt(num / den);
    var parfait = lignes.length >= PARFAIT_MIN_MESURES && lignes.every(function (l) { return Math.abs(l.d) <= PARFAIT_CM + 1e-9; });
    var niveau = parfait ? 'parfait' : (ecart <= 0.6 ? 'tres-proche' : (ecart <= 1.1 ? 'proche' : (ecart <= 1.8 ? 'a-considerer' : 'eloigne')));
    lignes.sort(function (a, b) { return b.n - a.n; });
    return { ecart: ecart, niveau: niveau, lignes: lignes };
  }

  /* Classement : « Comme un gant » d'abord, puis du plus proche au plus éloigné. */
  function ordre(a, b) {
    var pa = a.r.niveau === 'parfait' ? 0 : 1, pb = b.r.niveau === 'parfait' ? 0 : 1;
    return (pa - pb) || (a.r.ecart - b.r.ecart);
  }

  /* ---------- Mémoire du vêtement préféré (sur l'appareil) ---------- */

  function lireRef() {
    try {
      var s = window.localStorage.getItem(CLE_STOCKAGE);
      if (!s) return null;
      var o = JSON.parse(s);
      return o && o.groupe && o.mesures ? o : null;
    } catch (e) { return null; }
  }

  function ecrireRef(groupe, mesures) {
    try {
      window.localStorage.setItem(CLE_STOCKAGE, JSON.stringify({ groupe: groupe, mesures: mesures, date: Date.now() }));
      return true;
    } catch (e) { return false; }
  }

  function effacerRef() {
    try { window.localStorage.removeItem(CLE_STOCKAGE); } catch (e) { /* rien */ }
  }

  /* ---------- Chargement du vestiaire ---------- */

  /* Ce que le comparateur a dû écarter au dernier chargement (visible avec ?diagnostic). */
  var diagnostic = { rejets: [] };

  function attendre(ms) { return new Promise(function (ok) { setTimeout(ok, ms); }); }

  /* Pas d'en-tête « Accept: application/json » : Shopify répondrait alors par sa propre fiche JSON
     (collection ou produit) et ignorerait ?view=, donc sans aucune pièce ni mesure. */
  function chargerTexte(url, essais) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) {
        if (essais > 0 && (r.status === 429 || r.status >= 500)) {
          return attendre(900).then(function () { return chargerTexte(url, essais - 1); });
        }
        var e = new Error('HTTP ' + r.status);
        e.diag = 'Shopify répond ' + r.status + ' pour ' + url;
        throw e;
      }
      return r.text();
    });
  }

  function pasLaListe(nom) {
    var e = new Error(nom + ' n’est pas au format attendu');
    e.pasJSON = true;
    return e;
  }

  function lireJSON(url) {
    return chargerTexte(url, 1).then(function (t) {
      var debut = t.replace(/^\s+/, '');
      if (debut.charAt(0) !== '{') {
        var e = pasLaListe('La réponse');
        e.diag = url + ' ne renvoie pas de données (début : « ' + debut.slice(0, 60).replace(/\s+/g, ' ') + ' »)';
        throw e;
      }
      return JSON.parse(debut.slice(0, debut.lastIndexOf('}') + 1));
    });
  }

  /* Isole le premier objet { … } d'un texte, en respectant les chaînes. */
  function premierObjet(s) {
    var prof = 0, chaine = false, echap = false;
    for (var i = 0; i < s.length; i++) {
      var c = s.charAt(i);
      if (chaine) {
        if (echap) echap = false;
        else if (c === '\\') echap = true;
        else if (c === '"') chaine = false;
        continue;
      }
      if (c === '"') chaine = true;
      else if (c === '{') prof++;
      else if (c === '}') { prof--; if (prof === 0) return s.slice(0, i + 1); }
    }
    return s;
  }

  /* Lit une page de la liste. Si une fiche la rend illisible (par exemple un message
     « Liquid error » glissé dans les données), on lit les autres une par une et on écarte celle-là. */
  function lireListe(url) {
    return chargerTexte(url, 1).then(function (t) {
      var texte = t.replace(/^\s+/, '');
      if (texte.charAt(0) !== '{') {
        var e = pasLaListe('La liste des pièces');
        e.diag = url + ' renvoie une page normale au lieu de la liste : vérifiez templates/collection.mesures.liquid (début : « ' +
          texte.slice(0, 60).replace(/\s+/g, ' ') + ' »)';
        throw e;
      }
      try {
        var o = JSON.parse(texte.slice(0, texte.lastIndexOf('}') + 1));
        if (o && Array.isArray(o.pieces)) { o.rejets = []; return o; }
      } catch (err) { /* lecture fiche par fiche ci-dessous */ }
      var mPage = texte.match(/"page"\s*:\s*(\d+)/), mPages = texte.match(/"pages"\s*:\s*(\d+)/);
      var res = { page: mPage ? +mPage[1] : 1, pages: mPages ? +mPages[1] : 1, pieces: [], rejets: [] };
      texte.split(/(?=\{\s*"id"\s*:)/).slice(1).forEach(function (morceau) {
        try { res.pieces.push(JSON.parse(premierObjet(morceau))); }
        catch (err2) {
          var h = morceau.match(/"handle"\s*:\s*"([^"]*)"/);
          var tit = morceau.match(/"titre"\s*:\s*"([^"]*)"/);
          var liq = morceau.match(/Liquid error[^"\n<]{0,160}/);
          res.rejets.push({ handle: h ? h[1] : '?', titre: tit ? tit[1] : '', raison: liq ? liq[0] : 'données illisibles', page: res.page });
        }
      });
      if (!res.pieces.length) {
        var e2 = pasLaListe('La liste des pièces');
        e2.diag = 'Aucune fiche lisible sur ' + url + (res.rejets[0] ? ' (' + res.rejets[0].raison + ')' : '');
        throw e2;
      }
      return res;
    });
  }

  function avecParam(base, params) {
    var sep = base.indexOf('?') >= 0 ? '&' : '?';
    return base + sep + params;
  }

  function charger(base) {
    var cleCache = CLE_CACHE + ':' + base;
    diagnostic.rejets = [];
    try {
      var c = window.sessionStorage.getItem(cleCache);
      if (c) {
        var o = JSON.parse(c);
        if (o && o.pieces && o.pieces.length && Date.now() - o.t < DUREE_CACHE_MS) return Promise.resolve(o.pieces.map(normaliser));
      }
    } catch (e) { /* pas de cache */ }

    return lireListe(avecParam(base, 'view=mesures&page=1')).then(function (premier) {
      var pages = premier.pages || 1;
      var suite = [];
      for (var p = 2; p <= pages; p++) {
        suite.push(lireListe(avecParam(base, 'view=mesures&page=' + p)).catch(function (e) {
          return { pieces: [], rejets: [{ handle: '(une page entière)', titre: '', raison: e.diag || e.message }] };
        }));
      }
      return Promise.all(suite).then(function (autres) {
        var brutes = premier.pieces.slice();
        var rejets = (premier.rejets || []).slice();
        autres.forEach(function (a) { brutes = brutes.concat(a.pieces || []); rejets = rejets.concat(a.rejets || []); });
        diagnostic.rejets = rejets;
        if (rejets.length && window.console) console.warn('[L’Apprêt] Fiches écartées par le comparateur :', rejets);
        if (brutes.length && !rejets.length) {
          try { window.sessionStorage.setItem(cleCache, JSON.stringify({ t: Date.now(), pieces: brutes })); } catch (e) { /* plein */ }
        }
        return brutes.map(normaliser);
      });
    });
  }

  function chargerPiece(url, apercu) {
    return lireJSON(avecParam(url, 'view=studio' + (apercu ? '&' + apercu : ''))).then(function (o) {
      if (!o || !o.champs) throw pasLaListe('La fiche de la pièce');
      return normaliser(o);
    });
  }

  /* ---------- Affichage ---------- */

  function formatPrix(cents) {
    if (!estNombre(cents)) return '';
    var e = cents / 100;
    var entier = Math.round(e) === e;
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: entier ? 0 : 2, maximumFractionDigits: 2 }).format(e);
    } catch (err) {
      return (entier ? String(e) : e.toFixed(2).replace('.', ',')) + ' €';
    }
  }

  function el(tag, cls, txt) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt !== undefined && txt !== null) n.textContent = txt;
    return n;
  }

  /* ---------- Badge sur les fiches produit ---------- */

  function initBadges() {
    var blocs = document.querySelectorAll('[data-la-prefere]');
    if (!blocs.length) return;
    var ref = lireRef();
    Array.prototype.forEach.call(blocs, function (bloc) {
      var source = bloc.querySelector('script[type="application/json"]');
      var sortie = bloc.querySelector('[data-la-prefere-sortie]');
      if (!source || !sortie) return;
      var p;
      try { p = normaliser(JSON.parse(source.textContent)); } catch (e) { return; }
      if (!p.groupe) return;
      var lien = bloc.getAttribute('data-lien') || '/pages/votre-vetement-favori';
      var lienType = avecParam(lien, 'type=' + p.groupe);
      sortie.textContent = '';

      var r = ref && ref.groupe === p.groupe ? comparer(ref.mesures, p.m, p.groupe) : null;
      if (!r) {
        var a = el('a', 'la-prefere__invite', 'Comparer avec votre ' + GROUPES[p.groupe].nom + ' préféré →');
        a.href = lienType;
        sortie.appendChild(a);
        bloc.classList.remove('la-prefere--parfait');
        bloc.hidden = false;
        return;
      }

      bloc.classList.toggle('la-prefere--parfait', r.niveau === 'parfait');
      var tete = el('p', 'la-prefere__tete');
      var pastille = el('span', 'la-prefere__niveau la-prefere__niveau--' + r.niveau, NIVEAUX[r.niveau]);
      tete.appendChild(pastille);
      tete.appendChild(document.createTextNode(r.niveau === 'parfait'
        ? ' Les mesures de votre ' + GROUPES[p.groupe].nom + ' préféré, au centimètre près'
        : ' de votre ' + GROUPES[p.groupe].nom + ' préféré'));
      sortie.appendChild(tete);

      var details = el('details', 'la-prefere__details');
      details.appendChild(el('summary', null, 'Voir les écarts, mesure par mesure'));
      var ul = el('ul', 'la-prefere__lignes');
      r.lignes.forEach(function (l) {
        var li = el('li', 'la-prefere__ligne la-prefere__ligne--' + l.ton, l.texte);
        ul.appendChild(li);
      });
      details.appendChild(ul);
      var modif = el('a', 'la-prefere__modifier', 'Modifier mes mesures');
      modif.href = lienType;
      details.appendChild(modif);
      sortie.appendChild(details);
      bloc.hidden = false;
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initBadges);
  else initBadges();

  window.LaMesures = {
    CHAMPS: CHAMPS,
    GROUPES: GROUPES,
    NIVEAUX: NIVEAUX,
    IDS_MESURES: IDS_MESURES,
    nombre: nombre,
    normaliser: normaliser,
    groupeDe: groupeDe,
    aDesMesures: aDesMesures,
    comparer: comparer,
    ordre: ordre,
    yukiDe: yukiDe,
    charger: charger,
    diagnostic: diagnostic,
    chargerPiece: chargerPiece,
    lireRef: lireRef,
    ecrireRef: ecrireRef,
    effacerRef: effacerRef,
    formatPrix: formatPrix,
    fr: fr,
    el: el,
    initBadges: initBadges
  };
})();
